import React, { useState, useEffect, createContext } from 'react';
import { Tooltip } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { IMenuProps, SubMenuProps } from './types';
import './index.less';
import SubMenu from './subMenu';
import createGlobalState from 'react-use/esm/factory/createGlobalState';
import className from 'classnames';
import SkimMenu from './skimMenu';
import Quickstart, { QUICKSTART } from './quickstart';
import { useTranslation } from 'react-i18next';
import en_US from './locales/en_US.json';
import zh_HK from './locales/zh_HK.json';

interface Props {
  items: IMenuProps[];
  onClick: (key: string) => void;
  collapsed: string | null;
  switchCollapsed: () => void;
  defaultSelectedKeys?: string[];
  selectedKeys?: string[];
  quickIcon: React.ReactNode;
  quickActiveIcon?: React.ReactNode;
  fullModeWidth?: number;
}

export type { IMenuProps };

export const useMenuSelect = createGlobalState<string[]>([]);
export const useMenuActive = createGlobalState<string[]>([]);

var isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

export default function FcMenu(props: Props) {
  const {
    fullModeWidth,
    items,
    onClick,
    collapsed,
    switchCollapsed,
    defaultSelectedKeys,
    selectedKeys,
    quickIcon,
    quickActiveIcon,
  } = props;
  const [, setMenuActive] = useMenuActive();
  const [menuSelect, setMenuSelect] = useMenuSelect();
  const [shouldClick, setShouldClick] = useState(true);
  const [isQuickPanelVisible, setQuickPanelVisible] = useState(false);

  const { t, i18n } = useTranslation('fc-menu');

  useEffect(() => {
    i18n.addResourceBundle('en_US', 'fc-menu', en_US, true, true);
    i18n.addResourceBundle('zh_HK', 'fc-menu', zh_HK, true, true);
  }, []);

  useEffect(() => {
    if (shouldClick && menuSelect.length > 0) {
      if (menuSelect.includes(QUICKSTART)) {
        setQuickPanelVisible(true);
      } else {
        onClick(menuSelect[menuSelect.length - 1]);
        setMenuActive([]);
      }
    }
    setShouldClick(true);
  }, [menuSelect]);

  useEffect(() => {
    setShouldClick(false);
    if (defaultSelectedKeys && !selectedKeys) {
      setMenuSelect(defaultSelectedKeys);
    }
  }, []);

  useEffect(() => {
    setShouldClick(false);
    if (selectedKeys) {
      setMenuSelect(selectedKeys);
    }
  }, [selectedKeys]);

  const handleKeyPress = (e) => {
    if (e.ctrlKey && e.altKey && e.key === 'k' && !isMac) {
      setQuickPanelVisible(true);
    }
    if (e.metaKey && e.key === 'k' && isMac) {
      setQuickPanelVisible(true);
    }
    if (e.key === 'Escape') {
      setQuickPanelVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  return (
    <div
      className={className({
        'float-fc-menu': true,
        collapsed: collapsed === '1',
        hide: collapsed === '2',
      })}
      style={{ width: collapsed === '0' ? fullModeWidth : undefined }}
      onMouseLeave={() => {
        setMenuActive([]);
      }}
    >
      <div className='collapse-btn'>
        {collapsed === '2' ? (
          <Tooltip title={<SkimMenu items={items} />} placement='rightTop' overlayClassName='fc-skim-menu-tooltip'>
            <MenuFoldOutlined onClick={() => switchCollapsed()} />
          </Tooltip>
        ) : collapsed === '0' ? (
          <MenuFoldOutlined onClick={() => switchCollapsed()} />
        ) : (
          <MenuUnfoldOutlined onClick={() => switchCollapsed()} />
        )}
      </div>
      {collapsed !== '2' && (
        <>
          <div className='float-fc-menu-sub' style={{ height: 19 }}>
            <div className='fc-menu-sub-curve'></div>
          </div>
          <SubMenu
            item={{
              key: 'goto',
              label: t('快速跳转'),
              children: [{ label: isMac ? t('打开') + ' Cmd+K' : t('打开') + ' Ctrl+ALT+K', key: QUICKSTART }],
              icon: quickIcon,
              activeIcon: quickActiveIcon,
            }}
            collapsed={collapsed === '1'}
            key={'goto'}
            isQuickStart
          />
          <div className='float-fc-menu-divider float-fc-menu-sub'>
            <div className='fc-menu-sub-curve'></div>
          </div>
          {items
            .filter((item) => !!item)
            .map(
              (item) => item && <SubMenu item={item as SubMenuProps} collapsed={collapsed === '1'} key={item.key} />,
            )}
          <div className='float-fc-menu-sub' style={{ height: 19 }}>
            <div className='fc-menu-sub-curve'></div>
          </div>
        </>
      )}
      {isQuickPanelVisible && (
        <Quickstart
          visible={isQuickPanelVisible}
          onVisibleChange={(item) => {
            if (item) {
              setMenuSelect([item.primaryKey, item.key]);
            }
            setQuickPanelVisible(false);
          }}
          items={items}
        />
      )}
    </div>
  );
}
